import React, { useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { ShieldCheck, Save } from 'lucide-react';

const LABELS = {
  idle_timeout_min: { label: 'Idle timeout (auto-logout)', unit: 'minutes' },
  absolute_session_hours: { label: 'Absolute session lifetime', unit: 'hours' },
  sensitive_session_min: { label: 'Sensitive-access session duration', unit: 'minutes' },
  trusted_device_days: { label: 'Trusted device expiration', unit: 'days' },
};

const fmtChoice = (key, v) => {
  if (key === 'trusted_device_days' && v === 0) return 'Never (disabled)';
  if (key === 'absolute_session_hours' && v >= 24) return `${v / 24} day${v / 24 > 1 ? 's' : ''} (${v}h)`;
  return `${v} ${LABELS[key].unit}`;
};

export const SecurityConfigPanel = () => {
  const [settings, setSettings] = useState(null);
  const [allowed, setAllowed] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiCall('/api/admin/security-settings')
      .then((r) => { setSettings(r.settings); setAllowed(r.allowed); })
      .catch((e) => toast.error(e.message));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await apiCall('/api/admin/security-settings', { method: 'PUT', body: JSON.stringify(settings) });
      setSettings(r.settings);
      toast.success('Security policy updated — applies platform-wide immediately', { id: 'security-policy-saved' });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="security-config-panel">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-cyan-50 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-cyan-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Security Policy Configuration</h3>
          <p className="text-sm text-slate-500">Platform-wide session & authentication rules. No code changes required.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {Object.keys(LABELS).map((key) => (
          <div key={key}>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{LABELS[key].label}</label>
            <select
              value={settings[key]}
              onChange={(e) => setSettings({ ...settings, [key]: Number(e.target.value) })}
              className="w-full h-11 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:border-cyan-500"
              data-testid={`security-setting-${key}`}
            >
              {(allowed[key] || []).map((v) => (
                <option key={v} value={v}>{fmtChoice(key, v)}</option>
              ))}
            </select>
          </div>
        ))}
        <div className="flex items-center gap-3 md:col-span-2">
          <input
            type="checkbox"
            id="mfa-required-toggle"
            checked={!!settings.mfa_required}
            onChange={(e) => setSettings({ ...settings, mfa_required: e.target.checked })}
            data-testid="security-setting-mfa_required"
          />
          <label htmlFor="mfa-required-toggle" className="text-sm text-slate-700">
            Recommend MFA for all users (shows enrollment prompts on Security pages)
          </label>
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-4">
        Idle-logout warning is shown 2 minutes before expiry. Password complexity (8+ chars, upper/lower/number/special)
        and failed-login lockout (10 attempts / 60 min) are enforced by the platform validation framework.
      </p>

      <button onClick={save} disabled={busy}
              className="mt-4 h-11 px-5 rounded-full bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="security-config-save-btn">
        <Save className="w-4 h-4" /> {busy ? 'Saving…' : 'Save Security Policy'}
      </button>
    </div>
  );
};

export default SecurityConfigPanel;
